import { HumanReadableNumber } from './../../../shared/utils/humanReadableNumber.util';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { AdScheduleStatsCollection } from './../../../interfaces/adschedule';
import { AdServicesService } from './../../../services/ad-services.service';
import { DateRange } from 'app/interfaces/dateRange';
import { BbFilterConfiguration } from './../../shared/bb-filters/bb-filters.component';
import { TableRow, BbTableSelectOption } from './../../../services/bb-table.service';
import { Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { FilterSetService } from '../../../services/filter-set.service';
import { BbTableService } from '../../../services/bb-table.service';
import dayjs from 'dayjs';

@Component({
	selector: 'adschedule-list',
	templateUrl: './adschedule-list.component.html',
	styleUrls: ['./adschedule-list.component.scss'],
	providers: [BbTableService, FilterSetService],
})

export class AdScheduleListComponent extends AbstractListComponent implements OnInit, OnDestroy {
	putRightSub: Subscription;
	putRight: boolean;
	filterConfig: BbFilterConfiguration;
	statsDate: DateRange;
	lastStatsDate: DateRange;
	stats: any[];
	statsParams: any;
	adScheduleStats: AdScheduleStatsCollection = {};
	downloadingReport: boolean;

	constructor(
		protected bbTableService: BbTableService,
		public adServicesService: AdServicesService,
	) {
		super(bbTableService);

		this.listConfiguration = {
			name: 'adschedule',
			basePath: '/adservices/adschedule',
			activePath: '/adservices/adschedules',
			entityTranslation: this.translate.instant('AD_SCHEDULE').toLowerCase(),
			entitiesTranslation: this.translate.instant('AD_SCHEDULES').toLowerCase()
		};

		this.filterConfig = {
			search: true,
			filters: [],
			presets: [{
				label: this.translate.instant('ALL'),
				value: 'all',
				filterSet: [],
			},
			{
				label: this.translate.instant('PUBLISHED'),
				value: 'published',
				filterSet: [{
					filters: [{
						type: 'adschedule',
						field: 'status',
						operator: 'is',
						value: 'published'
					}]
				}],
			},
			{
				label: this.translate.instant('DRAFT'),
				value: 'draft',
				filterSet: [{
					filters: [{
						type: 'adschedule',
						field: 'status',
						operator: 'is',
						value: 'draft'
					}]
				}],
			}]
		};
	}

	ngOnInit() {
		this.bbTableService
			.setSelect(BbTableSelectOption.none)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		super.ngOnInit();

		this.subscriptions.add(
			this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
				this.rows = rows;
				this.lastStatsDate = null;
				this.updateAdScheduleStatsRows();
			})
		);

		this.subscriptions.add(this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight));
	}

	datesChanged(dateRange: DateRange) {
		this.statsDate = dateRange;

		const statsParams: any = {
			start: this.statsDate.startDate.toISOString(),
			end: this.statsDate.endDate.toISOString(),
		};

		// when requesting stats < 1 day, compare with previous day same time period (other cases handled by backend)
		if (Math.abs(dayjs(statsParams.start).diff(statsParams.end, 'hours')) <= 24) {
			statsParams.compare_start = dayjs(statsParams.start).subtract(1, 'days').toISOString();
			statsParams.compare_end = dayjs(statsParams.end).subtract(1, 'days').toISOString();
		}

		this.statsParams = statsParams;

		this.updateAdScheduleStatsRows();
	}

	compareNumbers(numberInput: number, numberCompare: number) {
		let comparison = '';
		if (numberInput > 0 && numberCompare > 0) {
			const percentualDifference = ((numberInput / numberCompare) - 1) * 100;
			if (percentualDifference < 100 && percentualDifference > -100) {
				comparison = percentualDifference.toFixed(1);
			} else {
				comparison = percentualDifference.toFixed(0);
			}
			let color = 'red';
			if (percentualDifference > 0) {
				color = 'green';
			}
			comparison = ' <span class="' + color + '"> (' + comparison + '%)</span>';
		} else {
			comparison = ' <span class="grey"> (n/a)</span>';
		}
		return comparison;
	}

	downloadFromUrl() {
		this.downloadingReport = true;
		let url = this.locationService.server + '/sapi/adschedulestats/download';
		let filename = 'adschedulestats-download';

		if (this.statsParams) {
			Object.keys(this.statsParams).forEach((key: string, i) => {
				url += (i === 0 ? '?' : '&') + key + '=' + this.statsParams[key];
			});
			if (this.statsParams.start) {
				filename += '-' + this.statsParams.start;
			}
			if (this.statsParams.end) {
				filename += '-' + this.statsParams.end;
			}
		}
		this.sapiService.downloadFromUrl(url, filename).subscribe(() => this.downloadingReport = false);
	}

	updateAdScheduleStatsRows() {
		if (this.rows?.length
			&& (!this.lastStatsDate || this.lastStatsDate.startDate !== this.statsDate.startDate || this.lastStatsDate.endDate !== this.statsDate.endDate)) {
			const adScheduleCodes = this.rows.map((row) => row.data.code);

			const params: any = { ...this.statsParams };
			params.adScheduleCodes = adScheduleCodes.join(',');
			if (!params.adScheduleCodes) {
				return;
			}
			this.sapiService.getUrl('/adschedulestats', params).subscribe((adScheduleStats: any) => {
				this.adScheduleStats = adScheduleStats as AdScheduleStatsCollection;
				this.rows.forEach((row: any) => {
					const stats = this.adScheduleStats[row?.data?.code];
					if (stats) {
						row.inview = HumanReadableNumber.transform(stats.inview) + this.compareNumbers(stats.inview, stats.inview_compare);
						row.inventory = HumanReadableNumber.transform(stats.inventory) + this.compareNumbers(stats.inventory, stats.inventory_compare);
						row.views = HumanReadableNumber.transform(stats.views) + this.compareNumbers(stats.views, stats.views_compare);
						row.placementRate = HumanReadableNumber.transform(stats.placementRate)
							+ '%' + this.compareNumbers(stats.placementRate, stats.placementRate_compare);
					} else {
						row.inview = 0 + ' (n/a)';
						row.inventory = 0 + '(n/a)';
						row.views = 0 + '(n/a)';
						row.placementRate = 0 + '(n/a)';
					}
				});
			});

			this.lastStatsDate = bbDeepCopy(this.statsDate);
		}
	}

	// override super doubleclick redirect:
	redirectToDetails(row: TableRow) {
		if (row && row.data && row.data.id) {
			this.router.navigate(['adservices/adschedule', row.data.id, 'metadata']);
		}
	}
}
